// Shared workout statistics used by the dashboard and the progress page

const LB_TO_KG = 0.45359237;

// Volume in kg (load × reps), converting sets logged in lbs so mixed units add up
export const computeSessionVolume = (exercises) => {
  return exercises.reduce((total, exercise) => {
    if (!exercise?.sets?.length) return total;
    return total + exercise.sets.reduce((sum, set) => {
      const weight = parseFloat(set.weight) || 0;
      const reps = parseInt(set.reps) || 0;
      const kg = set.weightUnit === 'lbs' || set.weightUnit === 'lb' ? weight * LB_TO_KG : weight;
      return sum + (weight > 0 ? kg * reps : 0);
    }, 0);
  }, 0);
};

export const getWeekBounds = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diffToMon = day === 0 ? -6 : 1 - day;
  const mon = new Date(d);
  mon.setDate(d.getDate() + diffToMon);
  mon.setHours(0, 0, 0, 0);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  sun.setHours(23, 59, 59, 999);
  return { start: mon, end: sun };
};

const byName = (a, b) => (a.attributes?.name || '').localeCompare(b.attributes?.name || '');

// The workout after the last completed one (A -> B -> C -> back to A); Treino A when nothing is logged yet
export const getNextWorkout = (workouts, logs) => {
  if (!workouts?.length) return null;
  const sorted = [...workouts].sort(byName);
  const lastWorkoutId = logs?.[0]?.attributes?.workout?.data?.id;
  const lastIndex = sorted.findIndex((workout) => workout.id === lastWorkoutId);
  return lastIndex === -1 ? sorted[0] : sorted[(lastIndex + 1) % sorted.length];
};

const dayKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

// Consecutive days with at least one completed session, counting back from today (or yesterday)
export const computeStreakDays = (logs, now = new Date()) => {
  const trainedDays = new Set((logs || []).map((log) => dayKey(log.attributes?.completedAt)));
  const cursor = new Date(now);
  if (!trainedDays.has(dayKey(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
  }
  let streak = 0;
  while (trainedDays.has(dayKey(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
};

// Sessions, volume and time (seconds) this week; the target is the number of workouts in the user's plan (max 7)
export const getWeekSummary = (logs, workoutCount, now = new Date()) => {
  const { start, end } = getWeekBounds(now);
  const weekLogs = (logs || []).filter((log) => {
    const completedAt = new Date(log.attributes?.completedAt);
    return completedAt >= start && completedAt <= end;
  });
  return {
    sessions: weekLogs.length,
    target: Math.min(workoutCount || 0, 7),
    volume: weekLogs.reduce((total, log) => total + computeSessionVolume(log.attributes?.exercises || []), 0),
    duration: weekLogs.reduce((total, log) => total + (log.attributes?.duration || 0), 0),
  };
};

// This week day by day, Monday first: each day's sessions with their volume (kg) and time (seconds)
export const getWeekDays = (logs, now = new Date()) => {
  const { start } = getWeekBounds(now);
  const todayKey = dayKey(now);
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    const key = dayKey(date);
    const dayLogs = (logs || []).filter((log) => dayKey(log.attributes?.completedAt) === key);
    return {
      date,
      isToday: key === todayKey,
      isFuture: key !== todayKey && date > now,
      logs: dayLogs,
      volume: dayLogs.reduce((total, log) => total + computeSessionVolume(log.attributes?.exercises || []), 0),
      duration: dayLogs.reduce((total, log) => total + (log.attributes?.duration || 0), 0),
    };
  });
};

// The plan in the order it's trained, starting at the next workout: D, A, B, C when D is next
export const getRotation = (workouts, next) => {
  const sorted = [...(workouts || [])].sort(byName);
  const start = sorted.findIndex((workout) => workout.id === next?.id);
  return start <= 0 ? sorted : [...sorted.slice(start), ...sorted.slice(0, start)];
};

// Rough session length: each set takes about 40 seconds plus its rest interval
export const estimateWorkoutMinutes = (exercises) => {
  const seconds = (exercises || []).reduce((total, exercise) => {
    const sets = parseInt(exercise.series) || 0;
    const rest = parseInt(exercise.interval) || 60;
    return total + sets * (40 + rest);
  }, 0);
  return Math.max(5, Math.round(seconds / 60 / 5) * 5);
};

// Warm-up and feeder sets are saved as their own entries: flagged, and named "(Aquecimento)" / "(Preparo)"
const WARMUP_SUFFIX = /\((warmup|warm-up|aquecimento|feeder|preparo)\)/i;

export const isWarmupEntry = (exercise) => !!exercise?.warmup || WARMUP_SUFFIX.test(exercise?.name || '');

// The entries a workout's thumbnail strip draws from: the working sets, so the first lift's warm-ups
// don't fill every slot. A workout of only warm-ups still shows them.
export const getPreviewExercises = (exercises) => {
  const all = exercises || [];
  const working = all.filter((exercise) => !isWarmupEntry(exercise));
  return working.length ? working : all;
};

// 90 -> "1:30" (rest intervals, timers)
export const formatSeconds = (seconds) => `${Math.floor(seconds / 60)}:${String(Math.max(0, seconds) % 60).padStart(2, '0')}`;
